import React, { useState, ChangeEvent, FormEvent } from "react";
import useCsrfToken from "../hooks/useCsrfToken";
import { toast } from "react-toastify";

interface CourseFormData {
  code: string;
  name: string;
  description: string;
  capacity: number;
  instructor: string;
  credits: number;
  prerequisites: string;
  schedule_id: number;
}

const Dashboard: React.FC = () => {
  const { csrfToken } = useCsrfToken();
  const [courseIdToDelete, setCourseIdToDelete] = useState<string>("");
  const [formData, setFormData] = useState<CourseFormData>({
    code: "",
    name: "",
    description: "",
    capacity: 0,
    instructor: "",
    credits: 0,
    prerequisites: "",
    schedule_id: 0,
  });

  const handleChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]:
        name === "capacity" || name === "credits" || name === "schedule_id"
          ? Number(value)
          : value,
    });
  };

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();

    fetch("http://127.0.0.1:8000/api/courses/create/", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRFToken": csrfToken,
      },
      credentials: "include",
      body: JSON.stringify(formData),
    })
      .then((response) => response.json())
      .then((data) => {
        toast.success("Course created successfully");
      })
      .catch((error) => {
        toast.error("Failed to create course");
      });
  };

  const handleDelete = () => {
    fetch(`http://127.0.0.1:8000/api/courses/${courseIdToDelete}/delete/`, {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
        "X-CSRFToken": csrfToken,
      },
      credentials: "include",
    })
      .then((response) => {
        if (response.ok) {
          toast.success("Course deleted successfully");
        } else {
          toast.error("Failed to delete course");
        }
      })
      .catch((error) => {
        toast.error("Failed to delete course");
      });
  };

  return (
    <div className="container mx-auto p-4">
      <span className="text-2xl font-semibold">Create Course</span>
      <form onSubmit={handleSubmit} className="mt-4">
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700">
            Code:
          </label>
          <input
            type="text"
            name="code"
            value={formData.code}
            onChange={handleChange}
            className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
          />
        </div>
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700">
            Name:
          </label>
          <input
            type="text"
            name="name"
            value={formData.name}
            onChange={handleChange}
            className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
          />
        </div>
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700">
            Description:
          </label>
          <textarea
            name="description"
            value={formData.description}
            onChange={handleChange}
            className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
          />
        </div>
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700">
            Capacity:
          </label>
          <input
            type="number"
            name="capacity"
            value={formData.capacity}
            onChange={handleChange}
            className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
          />
        </div>
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700">
            Instructor:
          </label>
          <input
            type="text"
            name="instructor"
            value={formData.instructor}
            onChange={handleChange}
            className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
          />
        </div>
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700">
            Credits:
          </label>
          <input
            type="number"
            name="credits"
            value={formData.credits}
            onChange={handleChange}
            className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
          />
        </div>
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700">
            Prerequisites:
          </label>
          <input
            type="text"
            name="prerequisites"
            value={formData.prerequisites}
            onChange={handleChange}
            className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
          />
        </div>
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700">
            Schedule ID:
          </label>
          <input
            type="number"
            name="schedule_id"
            value={formData.schedule_id}
            onChange={handleChange}
            className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
          />
        </div>
        <button
          type="submit"
          className="w-full bg-indigo-600 text-white py-2 px-4 rounded-md shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:text-sm"
        >
          Create Course
        </button>
      </form>
      <div className="mt-8">
        <span className="text-2xl font-semibold">Delete Course</span>
        <div className="mt-4">
          <label className="block text-sm font-medium text-gray-700">
            Course ID to Delete:
          </label>
          <input
            type="text"
            value={courseIdToDelete}
            onChange={(e) => setCourseIdToDelete(e.target.value)}
            className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
          />
        </div>
        <button
          onClick={handleDelete}
          className="mt-4 w-full bg-red-600 text-white py-2 px-4 rounded-md shadow-sm hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 sm:text-sm"
        >
          Delete Course
        </button>
      </div>
    </div>
  );
};

export default Dashboard;
